import React, { useEffect, useState, useContext } from 'react';
import { format, addDays, startOfWeek, endOfWeek, startOfMonth, endOfMonth, addMonths, subMonths, isSameMonth, isToday, parseISO, differenceInMinutes, parse, isSameDay, subDays } from 'date-fns';
import { ChevronLeftIcon, ChevronRightIcon, ClockIcon, CalendarDaysIcon } from '@heroicons/react/20/solid';
import { AuthContext } from '../AuthContext';
import ShiftCalendar from './shiftcalendar';
import Standby from '../staff/standby'; // adjust path as needed

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

export default function Shifts() {
  const [shifts, setShifts] = useState([]);
  const [allShifts, setAllShifts] = useState([]);
  const [bankHolidays, setBankHolidays] = useState({});
  const [staffList, setStaffList] = useState([]);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState('daily'); // 'monthly', 'weekly', 'daily'
  const [selectedDay, setSelectedDay] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showStandby, setShowStandby] = useState(false);

  const user = useContext(AuthContext);
  const isManagerOrAdmin = ['manager', 'admin'].includes(String(user?.access || '').toLowerCase());
  const hasSelectedStaff = Number.isInteger(selectedStaff);
  const selectedStaffObj = staffList.find(s => s.id === selectedStaff);
  const selectedStaffName = selectedStaffObj ? selectedStaffObj.Name : '';

  useEffect(() => {
    fetchStaff();
  }, []);

  useEffect(() => {
    if (view === 'daily' || selectedStaff) {
      fetchShifts();
    }
  }, [selectedStaff, currentDate, view]);

  useEffect(() => {
    setSelectedDay(null);
  }, [view, currentDate]);

  const fetchStaff = async () => {
    try {
      const response = await fetch('https://api.insightos.co.uk/api/staffmanager', {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });
      if (!response.ok) throw new Error('Failed to fetch staff');
      const data = await response.json();
      const filteredStaff = Array.isArray(data.data)
        ? data.data.filter((staff) => String(staff?.Name || '').trim().toLowerCase() !== 'unknown')
        : [];
      setStaffList(filteredStaff);
    } catch (error) {
      console.error('Error fetching staff:', error);
    }
  };

  const fetchShifts = async () => {
    setLoading(true);
    try {
      let response;
      if (view === 'monthly') {
        const month = format(currentDate, 'M');
        const year = format(currentDate, 'yyyy');
        response = await fetch(`https://api.insightos.co.uk/api/monthlyshifts/${selectedStaff}?month=${month}&year=${year}`, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
        });
      } else {
        response = await fetch('https://api.insightos.co.uk/api/shifts', {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
        });
      }
      if (!response.ok) throw new Error('Failed to fetch shifts');
      const data = await response.json();
      const knownStaffIds = new Set(staffList.map((staff) => staff.id));
      const validShiftRows = Array.isArray(data.data)
        ? data.data.filter((shift) => knownStaffIds.has(shift.StaffId))
        : [];
      if (view === 'monthly') {
        setShifts(validShiftRows);
        try {
          const bankHolidayResponse = await fetch('https://api.insightos.co.uk/api/shifts', {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
          });
          if (bankHolidayResponse.ok) {
            const bankHolidayData = await bankHolidayResponse.json();
            setBankHolidays(bankHolidayData.bankHolidays || {});
          }
        } catch (bankHolidayError) {
          console.error('Error fetching bank holidays for monthly view:', bankHolidayError);
        }
      } else if (view === 'daily') {
        const filtered = validShiftRows.filter(shift => {
          const shiftDate = parseISO(shift.ShiftDate);
          return isSameDay(shiftDate, currentDate);
        });
        setShifts(filtered);
        setBankHolidays(data.bankHolidays || {});
      } else {
        setAllShifts(validShiftRows);
        const filtered = validShiftRows.filter(shift => shift.StaffId === selectedStaff);
        setShifts(filtered);
        setBankHolidays(data.bankHolidays || {});
      }
    } catch (error) {
      console.error('Error fetching shifts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenOptions = () => {
    const targetDate = selectedDay || currentDate;
    setSelectedDate(format(targetDate, 'dd-MM-yy'));
  };

  const getDayColor = (date) => {
    const dayShifts = shifts.filter(shift => {
      const shiftDate = parseISO(shift.ShiftDate);
      return isSameDay(shiftDate, date);
    });

    if (dayShifts.some(shift => shift.Sick === 1)) {
      return 'bg-gradient-to-br from-rose-600/90 to-red-500/90 border-white/20 text-white shadow-md';
    } else if (dayShifts.some(shift => shift.Holiday === 1)) {
      return 'bg-gradient-to-br from-indigo-900/90 to-indigo-700/90 border-white/20 text-white shadow-md';
    } else if (dayShifts.length > 0) {
      return 'bg-gradient-to-br from-emerald-600/90 to-emerald-500/90 border-white/20 text-white shadow-md';
    }
    return 'bg-white/75 border-indigo-200/70 text-indigo-900 hover:bg-white';
  };

  const isBankHoliday = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    const divisions = ['england-and-wales', 'scotland', 'northern-ireland'];
    for (const division of divisions) {
      if (bankHolidays[division]?.events) {
        if (bankHolidays[division].events.find(event => event.date === dateStr)) {
          return true;
        }
      }
    }
    return false;
  };

  const formatShiftTime = (time = '') => time?.toString().slice(0, 5);

  const renderWeekTable = () => {
    const weekStart = startOfWeek(currentDate, { weekStartsOn: 1 });
    const weekEnd = endOfWeek(currentDate, { weekStartsOn: 1 });
    const dateRange = [];
    let day = weekStart;
    while (day <= weekEnd) {
      dateRange.push(format(day, 'dd-MM-yy'));
      day = addDays(day, 1);
    }

    const shiftsGrouped = {};
    allShifts.forEach((shift) => {
      const staff = staffList.find(s => s.id === shift.StaffId);
      if (!staff) return;
      const staffName = staff.Name;
      const role = staff.Role;
      const shiftDate = format(parseISO(shift.ShiftDate), 'dd-MM-yy');
      if (!shiftsGrouped[staffName]) shiftsGrouped[staffName] = {};
      shiftsGrouped[staffName][shiftDate] = {
        timeRange: shift.StartTime + ' - ' + shift.EndTime,
        hoursWorked: shift.Hours,
        Role: role,
      };
    });

    const roleCountsByDate = dateRange.reduce((acc, date) => {
      acc[date] = {};
      Object.values(shiftsGrouped).forEach((staffShifts) => {
        const shiftDetails = staffShifts[date];
        if (shiftDetails) {
          const role = shiftDetails.Role;
          acc[date][role] = (acc[date][role] || 0) + 1;
        }
      });
      return acc;
    }, {});

    const allRoles = Array.from(new Set(Object.values(shiftsGrouped).flatMap(staffShifts => Object.values(staffShifts).map(s => s.Role))));

    return (
      <div className="mt-6 sm:mt-8 flow-root">
        <div className="-mx-2 sm:-mx-4 -my-2 overflow-x-auto">
          <div className="inline-block min-w-full py-2 align-middle px-2 sm:px-6 lg:px-8">
            <div className="overflow-hidden rounded-2xl border border-indigo-200/60 bg-gradient-to-r from-indigo-100/80 via-white/85 to-purple-100/80 backdrop-blur-xl shadow-[0_12px_30px_rgba(99,102,241,0.2)]">
              <table className="min-w-[700px] w-full divide-y divide-indigo-200/60">
                <thead className="bg-white/70">
                  <tr>
                    <th scope="col" className="sticky left-0 z-10 bg-white/90 py-2 sm:py-3.5 pl-3 sm:pl-4 pr-2 text-left text-xs sm:text-sm font-semibold text-indigo-700">
                      Role
                    </th>
                    {dateRange.map((date) => (
                      <th scope="col" key={date} className="py-2 sm:py-3.5 px-2 sm:px-3 text-left text-[11px] sm:text-sm font-semibold text-indigo-700 whitespace-nowrap">
                        {format(parse(date, 'dd-MM-yy', new Date()), 'EEE d MMM')}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-indigo-200/50 bg-white/70">
                  {allRoles.map((role) => (
                    <tr key={role}>
                      <td className="sticky left-0 z-10 bg-white/90 whitespace-nowrap py-3 sm:py-4 pl-3 sm:pl-4 pr-2 text-xs sm:text-sm font-medium text-indigo-900">
                        {role}
                      </td>
                      {dateRange.map((date) => (
                        <td key={`${date}-${role}`} className="whitespace-nowrap px-2 sm:px-3 py-3 sm:py-4 text-xs sm:text-sm text-indigo-900">
                          {roleCountsByDate[date][role] || 0}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderCalendar = () => {
    if (loading) {
      return (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
        </div>
      );
    }

    if (view === 'daily') {
      return (
        <div className="bg-gradient-to-r from-indigo-100/80 via-white/85 to-purple-100/80 backdrop-blur-xl rounded-2xl shadow-[0_12px_30px_rgba(99,102,241,0.2)] border border-indigo-200/60 p-3 sm:p-6">
          <div className="flex items-center justify-between mb-4 sm:mb-6">
            <button
              type="button"
              onClick={() => setCurrentDate(addDays(currentDate, -1))}
              className="h-8 w-8 sm:h-10 sm:w-10 inline-flex items-center justify-center rounded-lg sm:rounded-xl border border-indigo-200/70 bg-white/75 text-indigo-900 hover:bg-white focus:outline-none"
            >
              <span className="sr-only">Previous day</span>
              <ChevronLeftIcon className="h-4 w-4 sm:h-5 sm:w-5" aria-hidden="true" />
            </button>
            <h3 className="text-xs sm:text-lg font-semibold text-indigo-900 text-center">{format(currentDate, 'EEEE, MMMM d, yyyy')}</h3>
            <button
              type="button"
              onClick={() => setCurrentDate(addDays(currentDate, 1))}
              className="h-8 w-8 sm:h-10 sm:w-10 inline-flex items-center justify-center rounded-lg sm:rounded-xl border border-indigo-200/70 bg-white/75 text-indigo-900 hover:bg-white focus:outline-none"
            >
              <span className="sr-only">Next day</span>
              <ChevronRightIcon className="h-4 w-4 sm:h-5 sm:w-5" aria-hidden="true" />
            </button>
          </div>
          <div className="space-y-4">
            {shifts.length > 0 ? (
              shifts.map((shift, idx) => {
                const staff = staffList.find(s => s.id === shift.StaffId);
                return (
                  <div key={idx} className="border border-indigo-200/70 bg-white/75 backdrop-blur-sm rounded-xl p-4 shadow-sm">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-medium text-indigo-900">{staff ? staff.Name : 'Unknown Staff'}</p>
                        {staff && <p className="text-sm text-indigo-800">{staff.Role} - {staff.Department}</p>}
                        <p className="text-sm text-indigo-700">
                          {formatShiftTime(shift.StartTime)} - {formatShiftTime(shift.EndTime)}
                        </p>
                        <p className="text-xs font-semibold text-indigo-800">
                          Hours: {shift.Hours}
                        </p>
                        <div className="mt-2 flex flex-wrap items-center gap-2">
                          {Number(shift.Sick) === 1 && (
                            <span className="inline-flex rounded-lg bg-gradient-to-r from-rose-600 to-red-600 px-3 py-1 text-base font-bold text-white shadow-sm">
                              Sick Today
                            </span>
                          )}
                          {Number(shift.Holiday) === 1 && (
                            <span className="inline-flex rounded-lg bg-gradient-to-r from-sky-600 to-indigo-600 px-3 py-1 text-sm font-bold text-white shadow-sm">
                              Holiday Today
                            </span>
                          )}
                          {Number(shift.Cover) === 1 && (
                            <span className="inline-flex rounded-lg bg-gradient-to-r from-amber-500 to-orange-600 px-3 py-1 text-sm font-bold text-white shadow-sm">
                              Cover Shift
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="text-center py-12">
                <ClockIcon className="mx-auto h-12 w-12 text-indigo-400" />
                <h3 className="mt-2 text-sm font-medium text-indigo-900">No shifts</h3>
                <p className="mt-1 text-sm text-indigo-700">No shifts scheduled for this day.</p>
              </div>
            )}
          </div>
        </div>
      );
    }

    let startDate, endDate, title;
    if (view === 'monthly') {
      startDate = startOfWeek(startOfMonth(currentDate), { weekStartsOn: 1 });
      endDate = endOfWeek(endOfMonth(currentDate), { weekStartsOn: 1 });
      title = format(currentDate, 'MMMM yyyy');
    } else {
      startDate = startOfWeek(currentDate, { weekStartsOn: 1 });
      endDate = endOfWeek(currentDate, { weekStartsOn: 1 });
      title = `Week of ${format(startDate, 'MMMM d, yyyy')}`;
    }

    const days = [];
    let day = startDate;
    while (day <= endDate) {
      days.push(day);
      day = addDays(day, 1);
    }

    return (
      <div className="bg-gradient-to-r from-indigo-100/80 via-white/85 to-purple-100/80 backdrop-blur-xl rounded-2xl shadow-[0_12px_30px_rgba(99,102,241,0.2)] border border-indigo-200/60 p-2 sm:p-6">
        <div className="flex items-center justify-between mb-4 sm:mb-6">
          <button
            type="button"
            onClick={() => setCurrentDate(view === 'monthly' ? subMonths(currentDate, 1) : addDays(currentDate, -7))}
            className="h-7 w-7 sm:h-10 sm:w-10 inline-flex items-center justify-center rounded-lg sm:rounded-xl border border-indigo-200/70 bg-white/75 text-indigo-900 hover:bg-white focus:outline-none"
          >
            <span className="sr-only">Previous {view === 'monthly' ? 'month' : 'week'}</span>
            <ChevronLeftIcon className="h-4 w-4 sm:h-5 sm:w-5" aria-hidden="true" />
          </button>
          <h3 className="text-[10px] leading-4 sm:text-lg font-semibold text-indigo-900 text-center px-1 sm:px-2 max-w-[180px] sm:max-w-none">{title}</h3>
          <button
            type="button"
            onClick={() => setCurrentDate(view === 'monthly' ? addMonths(currentDate, 1) : addDays(currentDate, 7))}
            className="h-7 w-7 sm:h-10 sm:w-10 inline-flex items-center justify-center rounded-lg sm:rounded-xl border border-indigo-200/70 bg-white/75 text-indigo-900 hover:bg-white focus:outline-none"
          >
            <span className="sr-only">Next {view === 'monthly' ? 'month' : 'week'}</span>
            <ChevronRightIcon className="h-4 w-4 sm:h-5 sm:w-5" aria-hidden="true" />
          </button>
        </div>

        <div className="grid grid-cols-7 gap-0.5 sm:gap-1 mb-2 sm:mb-4 rounded-xl border border-indigo-200/60 bg-white/60 p-1">
          {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day) => (
            <div key={day} className="p-0.5 sm:p-2 text-left text-[10px] sm:text-sm font-medium text-indigo-700 pl-1 sm:pl-2">
              {day}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-7 gap-0.5 sm:gap-1 rounded-xl border border-indigo-200/60 bg-white/60 p-1">
          {days.map((day, dayIdx) => {
            let dayClass = getDayColor(day);
            if (selectedDay && isSameDay(day, selectedDay)) {
              dayClass = 'bg-gradient-to-br from-emerald-600/90 to-emerald-500/90 border-white/20 text-white shadow-lg shadow-emerald-900/35';
            } else if (isToday(day)) {
              dayClass = 'bg-gradient-to-br from-sky-600/90 to-sky-500/90 border-white/20 text-white animate-pulse shadow-lg shadow-sky-900/35';
            }
            return (
              <button
                key={day.toISOString()}
                type="button"
                onClick={() => setSelectedDay(day)}
                className={classNames(
                  'h-7 w-7 sm:h-9 sm:w-9 md:h-11 md:w-11 rounded-lg sm:rounded-xl text-center text-[10px] sm:text-sm font-medium focus:outline-none focus:ring-1 sm:focus:ring-2 focus:ring-inset focus:ring-indigo-500 relative border transition-all',
                  dayClass,
                  isSameMonth(day, currentDate) ? '' : 'text-gray-400'
                )}
              >
                {format(day, 'd')}
                {isBankHoliday(day) && (
                  <div className="absolute top-0.5 right-0.5 w-1.5 h-1.5 sm:w-2 sm:h-2 bg-yellow-400 rounded-full ring-1 ring-white"></div>
                )}
              </button>
            );
          })}
        </div>

        <div className="mt-4 rounded-xl border border-indigo-200/60 bg-white/60 p-2.5 sm:p-3 text-[11px] sm:text-xs text-indigo-800">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 sm:space-x-4">
            <div className="flex items-center">
              <div className="w-3 h-3 bg-green-500 rounded mr-1"></div>
              <span>Booked</span>
            </div>
            <div className="flex items-center">
              <div className="w-3 h-3 bg-indigo-600 rounded mr-1"></div>
              <span>Holiday</span>
            </div>
            <div className="flex items-center">
              <div className="w-3 h-3 bg-red-500 rounded mr-1"></div>
              <span>Sick</span>
            </div>
            <div className="flex items-center">
              <div className="w-2 h-2 bg-yellow-400 rounded-full mr-1"></div>
              <span>Bank Holiday</span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Top-level conditional rendering
  if (showStandby) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-indigo-100 via-sky-50 to-emerald-50">
        <div className="max-w-7xl mx-auto px-2 sm:px-6 lg:px-8 py-4 sm:py-8">
          <button
            onClick={() => setShowStandby(false)}
            className="mb-4 inline-flex items-center gap-2 rounded-lg border border-indigo-200 bg-white px-3 py-2 text-sm font-semibold text-indigo-900 shadow-sm hover:bg-indigo-50"
          >
            ← Back to Shifts
          </button>
          <Standby />
        </div>
      </div>
    );
  }

  if (selectedDate) {
    const formattedDateForAPI = format(parse(selectedDate, 'dd-MM-yy', new Date()), 'yyyy-MM-dd');
    return (
      <ShiftCalendar
        selectedDate={formattedDateForAPI}
        onBack={() => setSelectedDate(null)}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-indigo-100 via-sky-50 to-emerald-50">
      <div className="max-w-7xl mx-auto px-2 sm:px-6 lg:px-8 py-4 sm:py-8">
        <div className="bg-gradient-to-r from-indigo-100/80 via-white/85 to-purple-100/80 backdrop-blur-xl rounded-2xl shadow-[0_12px_30px_rgba(99,102,241,0.2)] border border-indigo-200/60 p-3 sm:p-6 mb-4 sm:mb-6">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-3 sm:mb-4">
            <h1 className="text-lg sm:text-2xl font-bold text-indigo-900 mb-3 sm:mb-0">Staff Shifts</h1>
            <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-4 w-full sm:w-auto">
              <button
                onClick={() => setShowStandby(true)}
                className="rounded-lg sm:rounded-xl border border-white/20 bg-gradient-to-br from-emerald-800/90 to-emerald-700/90 px-2 py-1.5 sm:px-3 sm:py-2 text-[11px] sm:text-sm font-semibold text-white shadow-lg shadow-emerald-950/35 hover:from-emerald-700/95 hover:to-emerald-600/95 transition-all"
              >
                Stand By
              </button>
              <select
                value={selectedStaff || ''}
                onChange={(e) => setSelectedStaff(parseInt(e.target.value, 10))}
                className="rounded-lg sm:rounded-xl border border-indigo-200/70 bg-white/80 px-2 py-1 sm:px-3 sm:py-2 text-[11px] sm:text-sm text-indigo-900 focus:border-indigo-400 focus:ring-indigo-400 w-full sm:w-auto"
              >
                <option value="">Select Staff</option>
                {staffList.map((staff) => (
                  <option key={staff.id} value={staff.id}>
                    {staff.Name}
                  </option>
                ))}
              </select>
              <div className="grid grid-cols-3 gap-1 sm:flex sm:space-x-2 sm:gap-0 w-full sm:w-auto">
                <button
                  onClick={() => setView('daily')}
                  className={classNames(
                    'px-1.5 py-1 sm:px-3 sm:py-2 rounded-lg sm:rounded-xl text-[10px] sm:text-sm font-medium flex-1 sm:flex-none',
                    view === 'daily' ? 'rounded-xl border border-white/20 bg-gradient-to-br from-indigo-900/90 to-indigo-700/90 text-white shadow-lg shadow-indigo-950/35' : 'rounded-xl border border-indigo-200/70 bg-white/75 text-indigo-900 hover:bg-white'
                  )}
                >
                  Daily
                </button>
                <button
                  onClick={() => setView('weekly')}
                  className={classNames(
                    'px-1.5 py-1 sm:px-3 sm:py-2 rounded-lg sm:rounded-xl text-[10px] sm:text-sm font-medium flex-1 sm:flex-none',
                    view === 'weekly' ? 'rounded-xl border border-white/20 bg-gradient-to-br from-indigo-900/90 to-indigo-700/90 text-white shadow-lg shadow-indigo-950/35' : 'rounded-xl border border-indigo-200/70 bg-white/75 text-indigo-900 hover:bg-white'
                  )}
                >
                  Weekly
                </button>
                <button
                  onClick={() => setView('monthly')}
                  className={classNames(
                    'px-1.5 py-1 sm:px-3 sm:py-2 rounded-lg sm:rounded-xl text-[10px] sm:text-sm font-medium flex-1 sm:flex-none',
                    view === 'monthly' ? 'rounded-xl border border-white/20 bg-gradient-to-br from-indigo-900/90 to-indigo-700/90 text-white shadow-lg shadow-indigo-950/35' : 'rounded-xl border border-indigo-200/70 bg-white/75 text-indigo-900 hover:bg-white'
                  )}
                >
                  Monthly
                </button>
              </div>
              {isManagerOrAdmin && (
                <button
                  onClick={handleOpenOptions}
                  className="rounded-lg sm:rounded-xl border border-white/20 bg-gradient-to-br from-indigo-900/90 to-indigo-700/90 px-2 py-1.5 sm:px-3 sm:py-2 text-[11px] sm:text-sm font-semibold text-white shadow-lg shadow-indigo-950/35 hover:from-indigo-800/95 hover:to-indigo-600/95 transition-all"
                >
                  Options
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-8">
          <div>
            {renderCalendar()}
          </div>
          {hasSelectedStaff && (
            <div className="bg-gradient-to-r from-indigo-100/80 via-white/85 to-purple-100/80 backdrop-blur-xl rounded-2xl shadow-[0_12px_30px_rgba(99,102,241,0.2)] border border-indigo-200/60 p-3 sm:p-6">
              <h2 className="text-base sm:text-lg font-semibold text-indigo-900 mb-3 sm:mb-4">Shift Details{selectedStaffName ? ` — ${selectedStaffName}` : ''}</h2>
              <div className="space-y-3 sm:space-y-4 overflow-hidden">
                {(() => {
                  const displayShifts = selectedDay ? shifts.filter(shift => isSameDay(parseISO(shift.ShiftDate), selectedDay)) : shifts;
                  const weekStart = startOfWeek(currentDate, { weekStartsOn: 1 });
                  const weekEnd = endOfWeek(currentDate, { weekStartsOn: 1 });
                  const weekShifts = shifts.filter(shift => {
                    const shiftDate = parseISO(shift.ShiftDate);
                    return shiftDate >= weekStart && shiftDate <= weekEnd;
                  });
                  const totalHours = weekShifts.reduce((sum, shift) => sum + parseFloat(shift.Hours || 0), 0);
                  return (
                    <>
                      {displayShifts.length > 0 ? (
                        (() => {
                          const items = [];
                          if (view === 'weekly') {
                            items.push(
                              <div key="total-hours" className="bg-white/80 border border-indigo-200/70 rounded-xl p-4 shadow-sm min-w-0 flex-shrink-0">
                                <div className="flex items-center">
                                  <ClockIcon className="h-6 w-6 text-indigo-600 mr-3" />
                                  <div>
                                    <p className="font-bold text-indigo-900 text-lg">Total Hours This Week</p>
                                    <p className="text-sm text-indigo-800 font-semibold">{totalHours} hours</p>
                                  </div>
                                </div>
                              </div>
                            );
                          }
                          displayShifts.forEach((shift, idx) => {
                            items.push(
                              <div key={idx} className={`${selectedDay ? "bg-gradient-to-br from-emerald-600/90 to-emerald-500/90 text-white rounded-xl p-3.5 sm:p-4 min-w-0 shadow-lg shadow-emerald-900/35 border border-white/20" : `bg-white/80 border border-indigo-200/70 rounded-xl p-3.5 sm:p-4 min-w-0 shadow-sm`}`}>
                                <div className="flex justify-between items-start">
                                  <div>
                                    <p className={selectedDay ? "font-bold text-white text-sm sm:text-base" : "font-semi-bold text-indigo-900 text-sm sm:text-base"}>
                                      <CalendarDaysIcon className={`h-5 w-5 inline mr-2 ${selectedDay ? 'text-white' : 'text-indigo-900'}`} />
                                      {format(parseISO(shift.ShiftDate), 'EEEE d MMM')}
                                    </p>
                                    <div className="mt-2 flex flex-wrap gap-1.5">
                                      <span className={selectedDay ? "inline-flex items-center rounded-full bg-white/20 px-2 py-0.5 text-[11px] font-semibold text-green-100" : "inline-flex items-center rounded-full bg-indigo-100 px-2 py-0.5 text-[11px] font-semibold text-indigo-800"}>
                                        <ClockIcon className="h-3.5 w-3.5 mr-1" />
                                        {formatShiftTime(shift.StartTime)} - {formatShiftTime(shift.EndTime)}
                                      </span>
                                      <span className={selectedDay ? "inline-flex items-center rounded-full bg-white/20 px-2 py-0.5 text-[11px] font-semibold text-green-100" : "inline-flex items-center rounded-full bg-sky-100 px-2 py-0.5 text-[11px] font-semibold text-sky-800"}>
                                        Hours: {shift.Hours}
                                      </span>
                                    </div>
                                    {shift.BankHoliday === 1 && <p className={selectedDay ? "text-xs text-green-200 font-semibold" : "text-xs text-indigo-600 font-semibold"}>Bank Holiday</p>}
                                    <div className="mt-2 flex flex-wrap gap-2">
                                      {Number(shift.Sick) === 1 && (
                                        <span className="inline-flex rounded-lg bg-gradient-to-r from-rose-600 to-red-600 px-3 py-1 text-base font-bold text-white shadow-sm">
                                          Sick Today
                                        </span>
                                      )}
                                      {Number(shift.Holiday) === 1 && (
                                        <span className="inline-flex rounded-lg bg-gradient-to-r from-sky-600 to-indigo-600 px-3 py-1 text-sm font-bold text-white shadow-sm">
                                          Holiday Today
                                        </span>
                                      )}
                                      {Number(shift.Cover) === 1 && (
                                        <span className="inline-flex rounded-lg bg-gradient-to-r from-amber-500 to-orange-600 px-3 py-1 text-sm font-bold text-white shadow-sm">
                                          Cover Shift
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          });
                          return <div className="space-y-3 pb-2">{items}</div>;
                        })()
                      ) : (
                        <div className="text-center py-12">
                          <ClockIcon className="mx-auto h-12 w-12 text-indigo-400" />
                          <h3 className="mt-2 text-sm font-medium text-indigo-900">No shifts</h3>
                          <p className="mt-1 text-sm text-indigo-700">No shifts scheduled for this period.</p>
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
            </div>
          )}
          {view === 'weekly' && renderWeekTable()}
        </div>
      </div>
    </div>
  );
}